import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import styles from "./UnitField.module.css";
import { ColorStyle } from "src/utils/Colors";
import { ErpType } from "src/network/User";
import { IUnitData } from "src/stores/TicketStore";

interface IUnitFieldProps {
    accountingRowIndex?: number;
}

const UnitFieldBase = (props: IUnitFieldProps) => {
    const { t } = useTranslation();
    const { accountingUnitSearchStore, invoiceStore, authStore } = useContext(RootStoreContext);

    const { accountingRowIndex } = props;

    const [displayUnitList, setDisplayUnitList] = useState<boolean>(false);
    const [lastScrollPositionYUnitItems, setLastScrollYPositionUnitItems] = useState<number>(0);

    const unitInputRef = useRef<HTMLInputElement>(null);

    const resetSearchQuery = useCallback(() => {
        console.log("resetSearchQuery called");
        // const currentSelectedUnitQueryString = accountingUnitSearchStore.getSelectedUnitQueryString(accountingRowIndex);
        // accountingUnitSearchStore.setCurrentUnitSearchQuery(currentSelectedUnitQueryString ?? "", accountingRowIndex);
        if (accountingRowIndex !== undefined) {
            invoiceStore.updateAccountingWithSelectedUnit(undefined, true, accountingRowIndex);
        }
    }, [invoiceStore, accountingRowIndex]);

    useEffect(() => {
        const handleClickOutsideUnitInput = (event: any) => {
            if (
                displayUnitList &&
                unitInputRef.current &&
                !unitInputRef.current.contains(event.target) &&
                !(event.target.classList.contains("list-item") && event.target.classList.contains("accounting-unit")) &&
                !(
                    event.target.parentElement.classList.contains("list-item") &&
                    event.target.parentElement.classList.contains("accounting-unit")
                ) &&
                !(event.target.localName === "path" || event.target.localName === "svg") &&
                !(
                    event.target.classList.contains("highlighted-text") &&
                    event.target.classList.contains("accounting-unit")
                )
            ) {
                setDisplayUnitList(false);

                if (
                    invoiceStore.currentInvoice?.isAccountingUnitSelected(accountingRowIndex) &&
                    accountingUnitSearchStore.currentUnitSearchQuery !==
                        accountingUnitSearchStore.selectedUnitQueryString
                ) {
                    resetSearchQuery();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutsideUnitInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideUnitInput);
        };
    }, [
        displayUnitList,
        resetSearchQuery,
        accountingUnitSearchStore.selectedUnitQueryString,
        accountingUnitSearchStore.currentUnitSearchQuery,
        invoiceStore.currentInvoice?.isAccountingUnitSelected,
        accountingRowIndex,
        invoiceStore.currentInvoice
    ]);

    const updateAccountingWithSelectedUnit = useCallback(
        async (unit: IUnitData) => {
            if (accountingRowIndex !== undefined) {
                invoiceStore.updateAccountingWithSelectedUnit(unit, true, accountingRowIndex);
            }
        },
        [invoiceStore, accountingRowIndex]
    );

    const onSelectUnitItem = useCallback(
        async (activeListItemIndex: number) => {
            const unitItems = accountingUnitSearchStore.filteredUnitData(accountingRowIndex);

            if (activeListItemIndex < 0 || activeListItemIndex >= unitItems.length) {
                return;
            }

            const selectedUnitItem = unitItems[activeListItemIndex];

            updateAccountingWithSelectedUnit(selectedUnitItem);
        },
        [accountingRowIndex, accountingUnitSearchStore, updateAccountingWithSelectedUnit]
    );

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "accounting-unit",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayUnitList,
        isListDisplayed: displayUnitList,
        onSelectItem: onSelectUnitItem,
        resetSearchQuery: resetSearchQuery,
        allowDefaultTabBehavior: true
    });

    const resetAmountOfItemsDisplayedInUnitList = useCallback(() => {
        setLastScrollYPositionUnitItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const onChangeUnitInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInUnitList();

            const inputUnit = e.target.value;
            accountingUnitSearchStore.setCurrentUnitSearchQuery(inputUnit, accountingRowIndex);
        },
        [accountingUnitSearchStore, resetAmountOfItemsDisplayedInUnitList, accountingRowIndex]
    );

    const onFocusUnitInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (
                invoiceStore.currentInvoice?.isAccountingUnitSelected(accountingRowIndex ?? 0) &&
                unitInputRef.current
            ) {
                unitInputRef.current.select();
            }

            resetAmountOfItemsDisplayedInUnitList();
            setDisplayUnitList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [
            resetAmountOfItemsDisplayedInUnitList,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            accountingRowIndex,
            invoiceStore.currentInvoice
        ]
    );

    const onClickUnitItem = useCallback(
        (unitData: IUnitData) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayUnitList(false);
            updateAccountingWithSelectedUnit(unitData);
        },
        [setActiveListItemIndex, NO_LIST_ITEM_SELECTED, updateAccountingWithSelectedUnit]
    );

    const onScrollUnitList = useCallback(
        async (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYUnitItems;

            const unitData = await accountingUnitSearchStore.filteredUnitData(accountingRowIndex);
            const hasMoreResulsToDisplay = invoiceStore.numberOfDisplayedItems < unitData.length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastScrollYPositionUnitItems(pixelsFromTop);
        },
        [lastScrollPositionYUnitItems, invoiceStore, accountingUnitSearchStore, accountingRowIndex]
    );

    const unitSelectionList = (
        <ul
            id="accounting-unit-dropdown-list"
            className={`dropdown-list accounting-unit krediflow ${
                authStore.user?.erpType === ErpType.IT2 ? "IT2" : "RIMO"
            }`}
            onScroll={onScrollUnitList}
        >
            {accountingUnitSearchStore
                .filteredUnitData(accountingRowIndex)
                .slice(0, invoiceStore.numberOfDisplayedItems)
                .map((unit, index) => {
                    const unitName = accountingUnitSearchStore.getFormattedUnitFieldValue(unit);

                    const currentUnitSearchQuery =
                        accountingUnitSearchStore.getCurrentUnitSearchQuery(accountingRowIndex);

                    const formattedUnitName = boldenMatchedSubstring(
                        currentUnitSearchQuery ?? "",
                        unitName,
                        "highlighted-text accounting-unit"
                    );

                    return (
                        <li
                            key={index}
                            className="d-flex align-items-center list-item accounting-unit"
                            onClick={onClickUnitItem(unit)}
                            id={`accounting-unit-list-item-${index}`}
                        >
                            <UI.Icon
                                icon={UI.SVGIcon.Units}
                                size={25}
                                circular
                                color={ColorStyle("grey")}
                                backgroundColor="#D2D5D9"
                                className="accounting-unit-icon"
                            />
                            <div
                                className={`accounting-unit-text ${styles.AccountingUnitText}`}
                                dangerouslySetInnerHTML={{
                                    __html: formattedUnitName
                                }}
                            />
                        </li>
                    );
                })}
        </ul>
    );

    return (
        <>
            <UI.Input
                ref={unitInputRef}
                label={t("screens.unit.object").toString()}
                type="text"
                autoComplete="off"
                value={accountingUnitSearchStore.getCurrentUnitSearchQuery(accountingRowIndex) ?? ""}
                onChange={onChangeUnitInput}
                onFocus={onFocusUnitInput}
                onKeyDown={onKeyDownFunction}
                disabled={invoiceStore.currentInvoice?.isAccountingRealestateSelected(accountingRowIndex) === false}
            />
            {displayUnitList ? unitSelectionList : undefined}
        </>
    );
};

export const UnitField = Sentry.withProfiler(observer(UnitFieldBase));
